import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { reportingStoreQueryKeys } from '@/common/api/reporting-store/queryKeys';
import type { ItemHealth } from '@/common/api/types/dashboard/health';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { HealthApi } from '@/features/insights-dashboard/api/health';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import { useTablePagedQuery } from '../hooks/useTablePagedQuery';

type HealthEntry = { progress: number | null; health: ItemHealth | null };

const ITEM_HEALTH_BATCH_SIZE = 200;
const ITEM_HEALTH_CONCURRENCY_LIMIT = 4;
const EMPTY_ENTRIES: ReadonlyMap<number, HealthEntry> = new Map();

const chunkIds = (ids: readonly number[]): number[][] => {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += ITEM_HEALTH_BATCH_SIZE) {
    chunks.push(ids.slice(i, i + ITEM_HEALTH_BATCH_SIZE));
  }
  return chunks;
};
const matchHealthKey = buildIdKeyMatcher(reportingStoreQueryKeys.health.byItemId);

const healthEntryFromSource = (
  _id: number,
  sourceData: ItemHealth | null | undefined,
): HealthEntry | undefined => {
  if (sourceData === undefined) return undefined;
  return { progress: sourceData ? sourceData.progress / 100 : null, health: sourceData };
};

type VisibleHealthContextType = {
  entriesById: ReadonlyMap<number, HealthEntry>;
};

const VisibleHealthContext = React.createContext<VisibleHealthContextType | undefined>(undefined);

export const useVisibleProgress = (id: number | undefined): number | null | undefined => {
  const context = React.useContext(VisibleHealthContext);
  if (!context || id === undefined) return undefined;
  const entry = context.entriesById.get(id);
  if (entry === undefined) return undefined;
  return entry.progress;
};

export const useVisibleItemHealth = (id: number | undefined): ItemHealth | null | undefined => {
  const context = React.useContext(VisibleHealthContext);
  if (!context || id === undefined) return undefined;
  const entry = context.entriesById.get(id);
  if (entry === undefined) return undefined;
  return entry.health;
};

export const VisibleHealthProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const fetchByIds = React.useCallback(
    async (ids: readonly number[]): Promise<ReadonlyMap<number, HealthEntry>> => {
      const chunks = chunkIds(ids);
      const result = new Map<number, HealthEntry>();

      for (let i = 0; i < chunks.length; i += ITEM_HEALTH_CONCURRENCY_LIMIT) {
        const batch = chunks.slice(i, i + ITEM_HEALTH_CONCURRENCY_LIMIT);
        const responses = await Promise.allSettled(
          batch.map((chunk) => HealthApi.getItemHealth({ itemIds: chunk })),
        );

        for (const [index, response] of responses.entries()) {
          const chunk = batch[index] ?? [];
          if (response.status === 'rejected') {
            for (const id of chunk) result.set(id, { progress: null, health: null });
            continue;
          }

          const byId = new Map<number, ItemHealth>(
            response.value.data.map((entry) => [entry.itemId, entry]),
          );
          for (const entry of response.value.data) {
            queryClient.setQueryData(reportingStoreQueryKeys.health.byItemId(entry.itemId), entry);
          }
          for (const id of chunk) {
            const entry = byId.get(id);
            result.set(id, {
              progress: entry ? entry.progress / 100 : null,
              health: entry ?? null,
            });
          }
        }
      }

      return result;
    },
    [queryClient],
  );

  const { loadedById } = useTablePagedQuery<HealthEntry>({
    providerName: commonQueryKeys.itemsTable.visibleProgress,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  useCrossCachePagesSync<HealthEntry, ItemHealth | null | undefined>({
    providerName: commonQueryKeys.itemsTable.visibleProgress,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: matchHealthKey,
    transform: healthEntryFromSource,
  });

  const value = React.useMemo<VisibleHealthContextType>(
    () => ({ entriesById: loadedById.size > 0 ? loadedById : EMPTY_ENTRIES }),
    [loadedById],
  );

  return <VisibleHealthContext.Provider value={value}>{children}</VisibleHealthContext.Provider>;
};
